<script setup lang="ts">
import { ChevronLeft, ChevronRight, ListFilter } from 'lucide-vue-next'
import type { ListFilterCriterion } from '@/types/listFilters'

const props = defineProps<{
  criteria: ListFilterCriterion[]
}>()

const { t } = useI18n()

const isOpen = ref(false)
const activeKey = ref<string | null>(null)
const direction = ref<'forward' | 'back'>('forward')

const activeCriterion = computed(() => props.criteria.find((criterion) => criterion.key === activeKey.value) ?? null)

watch(isOpen, (open) => {
  if (open) activeKey.value = null
})

const showCriterion = (criterion: ListFilterCriterion) => {
  direction.value = 'forward'
  activeKey.value = criterion.key
  criterion.onOpen?.()
}

const showCriteria = () => {
  direction.value = 'back'
  activeKey.value = null
}

const enterFromClass = computed(() =>
  direction.value === 'forward' ? 'translate-x-3 opacity-0' : '-translate-x-3 opacity-0'
)
const leaveToClass = computed(() =>
  direction.value === 'forward' ? '-translate-x-3 opacity-0' : 'translate-x-3 opacity-0'
)
</script>

<template>
  <DropdownMenu v-model:open="isOpen">
    <DropdownMenuTrigger as-child>
      <Button variant="outline" data-testid="list-filter-menu">
        <ListFilter />
        {{ t('global.filters.filter') }}
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start" class="w-72 overflow-hidden p-0">
      <Transition
        mode="out-in"
        enter-active-class="motion-safe:transition-[opacity,translate] motion-safe:duration-200 motion-safe:ease-out"
        leave-active-class="motion-safe:transition-[opacity,translate] motion-safe:duration-100 motion-safe:ease-in"
        :enter-from-class="enterFromClass"
        :leave-to-class="leaveToClass"
      >
        <div v-if="activeCriterion" :key="activeCriterion.key">
          <div class="border-b p-1">
            <DropdownMenuItem data-testid="list-filter-back" @select.prevent="showCriteria">
              <ChevronLeft />
              <span class="font-medium text-foreground">{{ activeCriterion.label }}</span>
            </DropdownMenuItem>
          </div>
          <ListFilterOptions :criterion="activeCriterion" />
        </div>
        <div v-else key="criteria" class="p-1">
          <DropdownMenuItem
            v-for="criterion in criteria"
            :key="criterion.key"
            :data-testid="`list-filter-${criterion.key}`"
            @select.prevent="showCriterion(criterion)"
          >
            <component :is="criterion.icon" />
            <span class="flex-1 text-foreground">{{ criterion.label }}</span>
            <Badge v-if="criterion.selected.length" variant="secondary" class="tabular-nums">
              {{ criterion.selected.length }}
            </Badge>
            <ChevronRight />
          </DropdownMenuItem>
        </div>
      </Transition>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
